// CeKKeU 2.0 — Phase 1 finance module.
// ALL raw amounts live ONLY in localStorage (client-side). Never sent to server.
// Versioned schema; migrations must be idempotent, backward-compatible, non-destructive.
import { loadAssessment } from "@/lib/storage";

const KEY = "fcs_finance_v1";
export const SCHEMA_VERSION = 2;

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw === null ? null : JSON.parse(raw);
  } catch {
    return null;
  }
}
function write(obj) {
  try {
    localStorage.setItem(KEY, JSON.stringify(obj));
  } catch {
    /* storage unavailable */
  }
  return obj;
}
function uid() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : "f_" + Date.now().toString(36) + Math.random().toString(36).slice(2);
}
const nowISO = () => new Date().toISOString();

export const INCOME_TYPES = ["salary", "business", "freelance", "commission", "rental", "investment", "bonus", "other"];
export const OWNERS = ["husband", "wife", "household", "other"];
export const FREQUENCIES = ["monthly", "weekly", "irregular", "one_time"];
export const EXPENSE_GROUPS = ["housing", "food", "transport", "children", "debt", "health", "lifestyle", "social", "utilities", "other"];
export const CLASSIFICATIONS = ["essential", "flexible", "lifestyle"];
export const DEBT_TYPES = ["mortgage", "vehicle", "credit_card", "personal_loan", "other"];

// Quick-entry categories -> group + default classification
export const QUICK_CATEGORIES = [
  { id: "food", label: "Makan", group: "food", classification: "essential" },
  { id: "shopping", label: "Belanja", group: "other", classification: "flexible" },
  { id: "transport", label: "Transport", group: "transport", classification: "essential" },
  { id: "housing", label: "Rumah", group: "housing", classification: "essential" },
  { id: "children", label: "Anak", group: "children", classification: "essential" },
  { id: "debt", label: "Cicilan", group: "debt", classification: "essential" },
  { id: "lifestyle", label: "Lifestyle", group: "lifestyle", classification: "lifestyle" },
  { id: "social", label: "Sosial", group: "social", classification: "flexible" },
  { id: "other", label: "Lainnya", group: "other", classification: "flexible" },
];

function empty() {
  const t = nowISO();
  return {
    schemaVersion: SCHEMA_VERSION,
    seededFromAssessment: false,
    household: { id: uid(), name: "", currency: "IDR", created_at: t, updated_at: t },
    incomeSources: [],
    recurringExpenses: [],
    transactions: [],
    debts: [],
    budgets: [],
    scenarios: [],
    emergencyFund: { current_amount: 0, updated_at: t },
    ui: { recapDismissedWeek: null, reminderAckDate: null },
  };
}

// Future-proof, non-destructive migration by version.
function migrate(data) {
  if (!data || typeof data !== "object") return null;
  let d = { ...data };
  if (!d.schemaVersion) d.schemaVersion = 1;
  // v1 -> v2: add per-category budgets + ui prefs (non-destructive).
  if (!Array.isArray(d.budgets)) d.budgets = [];
  if (!Array.isArray(d.scenarios)) d.scenarios = [];
  if (!d.ui || typeof d.ui !== "object") d.ui = { recapDismissedWeek: null, reminderAckDate: null };
  d.schemaVersion = SCHEMA_VERSION;
  return d;
}

export function loadFinance() {
  return migrate(read());
}

export function hasHousehold() {
  const d = loadFinance();
  return !!(d && d.household);
}

// Best-effort, idempotent one-time seed from the existing assessment. Never
// overwrites existing finance data. Assessment/report are left untouched.
export function ensureInitialized() {
  let d = loadFinance();
  if (d) return d;
  d = empty();
  const a = loadAssessment();
  if (a) {
    d.household.name = a.familyName || "";
    const push = (arr, v) => v && arr.push(v);
    // income (best-effort; guarded)
    if (Number(a.husbandIncome) > 0)
      d.incomeSources.push(src("Gaji Suami", "salary", "husband", Number(a.husbandIncome)));
    if (Number(a.wifeIncome) > 0)
      d.incomeSources.push(src("Gaji Istri", "salary", "wife", Number(a.wifeIncome)));
    (a.sideIncomes || []).forEach((s) => {
      const amt = Number(s && (s.amount ?? s));
      if (amt > 0) d.incomeSources.push(src(s.source || s.name || "Penghasilan Tambahan", "other", "household", amt, false));
    });
    if (Number(a.emergencyFund) > 0 || Number(a.emergency) > 0)
      d.emergencyFund.current_amount = Number(a.emergencyFund || a.emergency);
    void push;
    d.seededFromAssessment = true;
  }
  return write(d);
}

function src(name, type, owner, amount, is_recurring = true) {
  const t = nowISO();
  return { id: uid(), name, type, owner, amount, frequency: "monthly", is_recurring, expected_date: null, active: true, created_at: t, updated_at: t };
}

// ---------- generic list helpers ----------
function save(mutator) {
  const d = ensureInitialized();
  mutator(d);
  d.household.updated_at = nowISO();
  return write({ ...d });
}

export function setHouseholdName(name) {
  return save((d) => {
    d.household.name = name;
  });
}

// ---------- Transactions ----------
export function addTransaction({ type, amount, category, income_source = null, date, note = "", classification = null, amountExpr = null }) {
  const t = nowISO();
  return save((d) => {
    d.transactions.unshift({
      id: uid(), type, amount: Number(amount) || 0, category: category || null,
      income_source, date: date || t.slice(0, 10), note, classification, amountExpr,
      created_at: t, updated_at: t,
    });
  });
}
export function updateTransaction(id, patch) {
  return save((d) => {
    const i = d.transactions.findIndex((x) => x.id === id);
    if (i >= 0) d.transactions[i] = { ...d.transactions[i], ...patch, updated_at: nowISO() };
  });
}
export function deleteTransaction(id) {
  return save((d) => {
    d.transactions = d.transactions.filter((x) => x.id !== id);
  });
}

// ---------- Income sources ----------
export function upsertIncome(item) {
  return save((d) => {
    const t = nowISO();
    if (item.id) {
      const i = d.incomeSources.findIndex((x) => x.id === item.id);
      if (i >= 0) d.incomeSources[i] = { ...d.incomeSources[i], ...item, updated_at: t };
    } else d.incomeSources.push({ active: true, is_recurring: true, frequency: "monthly", created_at: t, updated_at: t, ...item, id: uid() });
  });
}
export function deleteIncome(id) {
  return save((d) => {
    d.incomeSources = d.incomeSources.filter((x) => x.id !== id);
  });
}

// ---------- Recurring expenses ----------
export function upsertRecurring(item) {
  return save((d) => {
    if (item.id) {
      const i = d.recurringExpenses.findIndex((x) => x.id === item.id);
      if (i >= 0) d.recurringExpenses[i] = { ...d.recurringExpenses[i], ...item };
    } else d.recurringExpenses.push({ active: true, frequency: "monthly", classification: "essential", ...item, id: uid() });
  });
}
export function deleteRecurring(id) {
  return save((d) => {
    d.recurringExpenses = d.recurringExpenses.filter((x) => x.id !== id);
  });
}

// ---------- Debts ----------
export function upsertDebt(item) {
  return save((d) => {
    if (item.id) {
      const i = d.debts.findIndex((x) => x.id === item.id);
      if (i >= 0) d.debts[i] = { ...d.debts[i], ...item };
    } else d.debts.push({ active: true, type: "other", ...item, id: uid() });
  });
}
export function deleteDebt(id) {
  return save((d) => {
    d.debts = d.debts.filter((x) => x.id !== id);
  });
}

// ---------- Emergency fund ----------
export function setEmergencyFund(amount) {
  return save((d) => {
    d.emergencyFund = { current_amount: Number(amount) || 0, updated_at: nowISO() };
  });
}

// ---------- Category budgets (monthly targets) ----------
export function upsertBudget(item) {
  return save((d) => {
    if (!Array.isArray(d.budgets)) d.budgets = [];
    const t = nowISO();
    const existing = item.id
      ? d.budgets.findIndex((x) => x.id === item.id)
      : d.budgets.findIndex((x) => x.category === item.category);
    if (existing >= 0) d.budgets[existing] = { ...d.budgets[existing], ...item, updated_at: t };
    else d.budgets.push({ created_at: t, updated_at: t, ...item, id: uid() });
  });
}
export function deleteBudget(id) {
  return save((d) => {
    d.budgets = (d.budgets || []).filter((x) => x.id !== id);
  });
}

// ---------- Weekly recap dismissal (client-only UI pref) ----------
export function dismissWeeklyRecap(weekStartISO) {
  return save((d) => {
    if (!d.ui || typeof d.ui !== "object") d.ui = {};
    d.ui.recapDismissedWeek = weekStartISO;
  });
}

// ---------- Logging reminder acknowledgement (client-only UI pref) ----------
export function acknowledgeReminder(dateISO) {
  return save((d) => {
    if (!d.ui || typeof d.ui !== "object") d.ui = {};
    d.ui.reminderAckDate = dateISO;
  });
}

// ---------- Saved simulator scenarios (params only; recomputed live) ----------
export function upsertScenario(item) {
  return save((d) => {
    if (!Array.isArray(d.scenarios)) d.scenarios = [];
    const t = nowISO();
    if (item.id) {
      const i = d.scenarios.findIndex((x) => x.id === item.id);
      if (i >= 0) d.scenarios[i] = { ...d.scenarios[i], ...item, updated_at: t };
    } else d.scenarios.push({ created_at: t, updated_at: t, ...item, id: uid() });
  });
}
export function deleteScenario(id) {
  if (!id) return loadFinance();
  return save((d) => {
    d.scenarios = (d.scenarios || []).filter((x) => x.id !== id);
  });
}

// ---------- Queries ----------
export function txInRange(transactions, range) {
  const now = new Date();
  const start = new Date(now);
  if (range === "day") start.setHours(0, 0, 0, 0);
  else if (range === "week") start.setDate(now.getDate() - 6);
  else start.setDate(1); // month
  start.setHours(0, 0, 0, 0);
  return (transactions || []).filter((t) => new Date(t.date) >= start);
}
